// @flow

import React, { Component } from 'react';
import { type Dispatch } from 'redux';

import { stateType } from '../../app/stateTypes';
import { setPictureInPictureModeConfig } from '../../base/config';
import { getPictureInPictureModeConfig } from '../../base/config/functions.any';
import { MEDIA_TYPE } from '../../base/media';
import { isLocalParticipantModerator } from '../../base/participants';
import { getTrackByMediaTypeAndParticipant } from '../../base/tracks';
import { E2EELabel } from '../../e2ee';
import { isFilmstripVisible } from '../../filmstrip';
import { LocalRecordingLabel } from '../../local-recording';
import { PictureInPictureLabel } from '../../picture-in-picture';
import { RecordingLabel } from '../../recording';
import { TranscribingLabel } from '../../transcribing';
import { shouldDisplayTileView } from '../../video-layout';
import { VideoQualityLabel } from '../../video-quality';
import { VoiceCommandLabel } from '../../voice-command';

import { InsecureRoomNameLabel } from '.';


/**
 * The type of the React {@code Component} props of {@link AbstractLabels}.
 */
export type Props = {

    /**
     * Whether the filmstrip is displayed with remote videos. Used to determine
     * display classes to set.
     */
    _filmstripVisible: boolean,

    /**
     * Whether the video quality label should be displayed.
     */
    _showVideoQualityLabel: boolean,

    _isVoiceCommandDisabled: boolean,
};

/**
 * A container to hold video status labels, including recording status and
 * current large video quality.
 *
 * @augments Component
 */
export default class AbstractLabels<P: Props, S> extends Component<P, S> {
    /**
 * Constructor for AbstractLabels
 *
 *
 * @augments Component
 */
    constructor(props) {
        super(props);

        // Access the translation function from props
        this.t = this.props.t;

        // ... your other constructor logic ...
    }

    /**
     * Renders the {@code E2EELabel}.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderE2EELabel() {
        return <E2EELabel />;
    }

    /**
     * Renders the {@code LocalRecordingLabel}.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderLocalRecordingLabel() {
        return <LocalRecordingLabel />;
    }

    /**
     * Renders the {@code RecordingLabel} that is platform independent.
     *
     * @param {string} mode - The recording mode that this label is rendered
     * for.
     * @protected
     * @returns {React$Element}
     */
    _renderRecordingLabel(mode: string) {
        return <RecordingLabel mode = { mode } />;
    }

    /**
     * Renders the {@code TranscribingLabel}.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderTranscribingLabel() {
        return <TranscribingLabel />;
    }

    /**
     * Renders the {@code InsecureRoomNameLabel}.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderInsecureRoomNameLabel() {
        return <InsecureRoomNameLabel />;
    }

    /**
     * Renders the {@code VideoQualityLabel} that is platform independent.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderVideoQualityLabel() {
        return <VideoQualityLabel />;
    }

    /**
     * Renders the {@code VoiceCommandLabel} that is platform independent.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderVoiceCommandLabel() {
        if (this.props._isVoiceCommandDisabled) {
            return;
        }

        return <VoiceCommandLabel />;
    }

    /**
     * Renders the {@code VoiceCommandLabel} that is platform independent.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderPictureInPictureLabel() {
        return <PictureInPictureLabel { ...this.props } />;
    }
}

// const PictureInPictureComponent = (props) => {
//     const { t } = useTranslation();
//     return (

//         <Tooltip
//             content = { props._isPictureInPictureVisibl ? t('voicecommand.inActivePIPLabel')  : t('voicecommand.activePIPLabel') }
//             position = { 'left' }>
//             <CircularLabel
//                 className = 'vc'
//                 icon = {props._isPictureInPictureVisible ? PictureInPictureExitIcon : PictureInPictureInIcon}
//                 onClick = {() => props.onClickedLabel(!props._isPictureInPictureVisible) } />
//         </Tooltip>

//     )
// }

/**
 * Maps (parts of) the redux state to the associated props of the {@link Labels}
 * {@code Component}.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {{
 *     _filmstripVisible: boolean,
 *     _showVideoQualityLabel: boolean
 * }}
 */
export function _abstractMapStateToProps(state: Object) {
    // const { confSettings } = state['features/base/jwt'];
    const { interfaceConfig } = state[stateType.config];
    const isPersonModerator = isLocalParticipantModerator(state);

    // in future this will be based on jwt.
    const isVoiceCommandDisabled = Boolean(
        interfaceConfig?.DISABLE_VOICE_COMMAND || !isPersonModerator
    );
    const { participantId } = state['features/large-video'];
    const videoTrackOnLargeVideo = getTrackByMediaTypeAndParticipant(
        state['features/base/tracks'],
        MEDIA_TYPE.VIDEO,
        participantId
    );

    return {
        _filmstripVisible: isFilmstripVisible(state),
        _showVideoQualityLabel: !shouldDisplayTileView(state),
        _isVoiceCommandDisabled: isVoiceCommandDisabled,
        _isPictureInPictureVisible: getPictureInPictureModeConfig(state),
        _videoTrack: videoTrackOnLargeVideo
    };
}

/**
 * Maps dispatching of some action to React component props.
 *
 * @param {Function} dispatch - Redux action dispatcher.
 * @private
 * @returns {{
 *     _onUnmount: Function
 * }}
 */
export function _abstractMapDispatchToProps(dispatch: Dispatch<any>) {
    return {
        /**
         * Dispatches actions to store picture in picture mode config.
         *
         * @param {string} streamId - The ID of the stream.
         * @param {Transform} transform - The last applied transform.
         * @private
         * @returns {void}
         */
        onClickedLabel(value) {
            dispatch(setPictureInPictureModeConfig(value));
        }
    };
}
